import React from 'react'

import { DocumentORM } from 'src/types/types'
import { DNARowsComponent } from 'src/components/DNA/FlatList/Rows/types'
import { DNABox, DNACard, DNAText, DNAButton, useTheme, Iffy, DNAChip } from '@alucio/lux-ui'
import DNADocumentThumbnail from 'src/components/DNA/Document/DNADocumentThumbnail'
import DNADocumentChip from 'src/components/DNA/Document/DNADocumentChip'
import DNADocumentContextMenu from 'src/components/DNA/Document/DNADocumentContextMenu/DNADocumentContextMenu'
import { DocumentAccessLevel, DocumentStatus, PurposeType } from '@alucio/aws-beacon-amplify/src/models'
import { format } from 'date-fns';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { StyleSheet } from 'react-native';

export type DNADocumentDefaultRowProps = {
  onPin?: () => void,
  isDesktop?: boolean;
}

const styles = StyleSheet.create({
  containerStyle: {
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    borderRadius: 4,
    marginVertical: 4,
  },
})

const DNADocumentDefaultRow: DNARowsComponent<
  DocumentORM,
  DNADocumentDefaultRowProps
> = (props) => {
  const {
    ORM: documentORM,
    onPress,
    style,
    isDesktop,
  } = props

  const theme = useTheme()
  const isPersonalDocument = documentORM.model.accessLevel === DocumentAccessLevel.USER;

  const documentVersion = documentORM.relations.version.latestUsableDocumentVersion.model;
  const isInteractive = documentVersion.purpose !== PurposeType.INTERNAL_USE_ONLY &&
    documentVersion.status !== DocumentStatus.ARCHIVED;

  return (
    <DNACard
      appearance="meetings"
      interactive={isInteractive ? 'pressable' : undefined}
      onPress={() => { onPress?.(documentORM) }}
      style={[
        {
          paddingHorizontal: theme['row-padding-horizontal'],
          paddingVertical: theme['row-padding-vertical'],
        },
        style,
        styles.containerStyle,
        !isInteractive && { opacity: 0.4 },
      ]}
    >
      <DNABox alignY="center" spacing="between" childFill={0}>
        <DNABox alignY="center" spacing="large" childFill={1}>
          <DNABox appearance="row" style={{ borderWidth: 1, borderColor: theme['color-flat-300'] }}>
            <Iffy is={!isDesktop}>
              <DNADocumentContextMenu documentORM={documentORM}>
                <DNAButton
                  status="subtle"
                  appearance="ghostAlt"
                  context={'roundTablet'}
                  iconLeft="dots-vertical"
                  size={'xlarge'}
                />
              </DNADocumentContextMenu>
            </Iffy>
            <DNADocumentThumbnail
              documentVersionORM={
                documentORM.relations.version.latestDocumentVersionPublished ||
                documentORM.relations.version.latestDocumentVersion
              }
              width={76}
              height={44}
              showProcessing
            />
          </DNABox>
          {/* Title */}
          <DNABox
            spacing="small"
            appearance="col"
            childFill
            style={{ paddingRight: 16 }}
          >
            <DNAText b1 numberOfLines={1} status="basic">
              {documentORM.relations.version.latestDocumentVersionPublished?.model.title}
            </DNAText>
            {!isPersonalDocument &&
              <DNABox alignY="center" spacing="small" fill>
                <DNADocumentChip
                  item={documentORM}
                  variant="purpose"
                />
                <DNAChip appearance="tag" status="flat">
                  { documentORM.model.type }
                </DNAChip>
              </DNABox>}
          </DNABox>
        </DNABox>
        {/* Actions */}
        <DNABox spacing="small" alignY="center">
          <Iffy is={isPersonalDocument}>
            <DNAText style={{ color: colors['color-text-secondary'], marginRight: 57 }}>
              {format(new Date(documentORM.model.createdAt), 'MMM dd, yyyy')}
            </DNAText>
          </Iffy>
        </DNABox>
      </DNABox>
    </DNACard>
  )
}

export default DNADocumentDefaultRow
